import I18n from 'i18n-js';
import React, { useContext, useState } from 'react';
import { keys, filter, includes } from 'lodash';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { gql } from '@reverbdotcom/commons/src/gql';
import { RCLoadingBars, RCText, RCTextWithIcon, RCTag } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID } from '@reverbdotcom/commons/src/constants';
import { ThumbsUpIcon } from '@reverbdotcom/cadence/icons/react';

import { SellFormContext } from '../../SellFormContext';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';
import {
  carrierCalculatedPackageValidator,
  oversizePackageWarningKey,
  isOversizedDimensionsLabel,
  REGULAR_SIZED_LONGEST_EDGE_THRESHOLD,
  OVERSIZED_LONGEST_EDGE_THRESHOLD,
} from './carrierCalculatedPackageValidator';
import { Field } from '../../sellFormReducerTypes';
import { SelectedCarrier } from './SelectedCarrier';

export default function CarrierCalculatedEstimator({ ccValueExpEnabled = false }: { ccValueExpEnabled?: boolean }) {
  const { listingEntry, shopConfig } = useContext(SellFormContext);

  const hasGuitarRootCategory = [ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID].includes(listingEntry[Field.ROOT_CATEGORY_UUID]);
  const [shippingEstimatesResponse, setShippingEstimatesResponse] = useState([]);

  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const validPackageEntry = packageEntryComplete(packageEntry) && !carrierCalculatedPackageValidator(packageEntry)?.errorTranslationKey;
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const shouldFetchEstimate = shopConfig.currencySetting === 'USD' &&
                                validPackageEntry &&
                                !!currentPriceInput &&
                                currentPriceCents > 0;
  const isOversizedDimensions = oversizePackageWarningKey(packageEntry) === isOversizedDimensionsLabel;
  const overrideComparisonPackageDimension = () => {
    if (!shouldFetchEstimate) return {};
    const longestSideKey = filter(keys(packageEntry), (key) => includes(['length', 'width', 'height'], key)).sort((a, b) => packageEntry[b]?.value - packageEntry[a]?.value)[0];

    return { [longestSideKey]: { value: `${isOversizedDimensions ? REGULAR_SIZED_LONGEST_EDGE_THRESHOLD : OVERSIZED_LONGEST_EDGE_THRESHOLD}`, unit: 'in' } };
  };
  const userInputPackageDetails = {
    packageEntry,
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };
  const comparisonPackageDetails = {
    packageEntry: {
      ...packageEntry,
      ...overrideComparisonPackageDimension(),
    },
    itemPrice: {
      amount: currentPriceInput,
      currency: shopConfig.currencySetting,
    },
    categoryRootUuid: listingEntry[Field.ROOT_CATEGORY_UUID],
    categoryUuids: listingEntry[Field.SUBCATEGORY_LEAF_UUIDS],
  };

  const { loading } = useQuery(
    FETCH_SHIPPING_ESTIMATES,
    {
      skip: !shouldFetchEstimate,
      variables: {
        input: {
          packageDetails: [
            userInputPackageDetails,
            comparisonPackageDetails,
          ],
        },
      },
      onCompleted: ({ shippingEstimates }) => setShippingEstimatesResponse(shippingEstimates.estimates),
    },
  );

  const estimateAvailable = !loading && shippingEstimatesResponse?.[0]?.priceLow?.display;
  const userInputEstimate = shippingEstimatesResponse?.[0];
  const comparisonEstimate = shippingEstimatesResponse?.[1];

  if (!shouldFetchEstimate) { return null; }
  if (!validPackageEntry) { return null; }

  return (
    <>
      {loading && (
        <div className="d-flex fx-align-center fx-justify-center mt-6 mb-2">
          <RCLoadingBars />
        </div>
      )}

      {!loading && estimateAvailable && (
        <div className="mt-4">
          {hasGuitarRootCategory && (
            <div className="d-flex fx-dir-col fx-align-start gap-4 mt-6">
              <RCText.Utility size="400" weight="bold">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHint')}
              </RCText.Utility>

              <div className="carrier-calculated-estimator__guitar-wrapper">
                <div className="carrier-calculated-estimator__guitar-comparison-box">
                  <RCText.Utility size="350">
                    {I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'oversizedBoxDimensions' : 'standardBoxDimensions' }`)}
                  </RCText.Utility>
                  <RCText.Utility size="500" weight="bold">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                      lowDisplay: userInputEstimate.priceLow.display,
                      highDisplay: userInputEstimate.priceHigh.display,
                    })}
                  </RCText.Utility>
                  <RCTag variant={`${isOversizedDimensions ? 'default' : 'highlight' }`}>
                    <span className="tt-upercase">{I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'oversizedBoxSizeTag' : 'standardBoxSizeTag' }`)}</span>
                  </RCTag>
                </div>
                <div className="carrier-calculated-estimator__guitar-comparison-box">
                  <RCText.Utility size="350">
                    {I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.${isOversizedDimensions ? 'standardBoxDimensionsTip' : 'oversizedBoxDimensionsTip' }`)}
                  </RCText.Utility>
                  <RCText.Utility size="400" weight="semibold">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                      lowDisplay: comparisonEstimate.priceLow.display,
                      highDisplay: comparisonEstimate.priceHigh.display,
                    })}
                  </RCText.Utility>
                </div>
              </div>
            </div>
          )}

          {!hasGuitarRootCategory && (
            <>
              {ccValueExpEnabled && (
                <div className="padding-3 bd-radius-md bg-color-gray-200">
                  <div className="d-grid gap-gutter grid-cols-12">
                    <div className="grid-col-span-4 mobile-grid-col-span-12 fx-align-self-center">
                      <SelectedCarrier carrier={packageEntry.allowedCarriers[0]} />
                    </div>
                    <div className="grid-col-span-8 mobile-grid-col-span-12">
                      <div className="padding-4 bd-radius-md bg-white">
                        <p className="mb-half">
                          {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHint')}
                        </p>

                        <div className="d-flex fx-wrap fx-align-center">
                          <div className="mr-2 size-120 weight-bold ws-nowrap">
                            {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                              lowDisplay: userInputEstimate.priceLow.display,
                              highDisplay: userInputEstimate.priceHigh.display,
                            })}
                          </div>
                          <div
                            className="padding-2 bd-radius-md size-90 mobile-mt-2"
                            style={{ backgroundColor: 'var(--rc-color-background-success-ultralight)' }}
                          >
                            <RCTextWithIcon icon={<ThumbsUpIcon />} placement="left" size="large">
                              <span className="weight-bold mr-1">
                                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.greatPrice')}
                              </span>
                              <span>
                                {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyersSave')}
                              </span>
                            </RCTextWithIcon>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!ccValueExpEnabled && (
                <div className="d-flex fx-dir-row mobile-fx-dir-col fx-align-center gap-4 mt-6">
                  <RCText.Utility size="400" weight="bold">
                    {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostHint')}
                  </RCText.Utility>

                  <div>
                    <strong>
                      {I18n.t('discovery.sellForm.shippingSection.carrierCalculatedForm.buyerCostRange', {
                        lowDisplay: userInputEstimate.priceLow.display,
                        highDisplay: userInputEstimate.priceHigh.display,
                      })}
                    </strong>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

export const FETCH_SHIPPING_ESTIMATES = gql(`
  query Core_SellForm_FetchShippingEstimates($input: InputShippingEstimatesRequest!) {
    shippingEstimates(input: $input) {
      estimates {
        priceLow {
          amountCents
          display
        }
        priceHigh {
          amountCents
          display
        }
      }
    }
  },
`);
